import {
  ConfigurationId,
  FundShareClassId,
  GeneralConfig,
  LiteratureData,
  LiteratureDocumentData,
  PerformanceConfig,
  PerformanceViewOption,
  ProductData,
  ProductType,
} from '@types';

import { BasePpssService } from './base-ppss.service';
import { FundFilterService } from './fund-filter.service';
import { FundListing } from '@models';
import { INITIAL_STATE } from './gw.ppss.config';
import { Injectable } from '@angular/core';
import { Logger } from '@utils/logger';
import { PPSSFundDataService } from '@products/services/ppss-fund-data.service';
import { SiteConfigService } from '@services/site-config.service';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { take } from 'rxjs/operators';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { AppStateService } from '@services/app-state.service';
import { combineLatest } from 'rxjs';
import { SegmentService } from '@services/segment.service';
import { DebugService } from '@services/debug.service';
import cloneDeep from 'lodash/cloneDeep';
import { FavoritesService } from '@products/services/favorites.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';

const logger = Logger.getLogger('PpssService');

@Injectable()
export class PpssService extends BasePpssService {
  constructor(
    productType: ConfigurationId,
    private fundDataService: PPSSFundDataService,
    filterService: FundFilterService,
    protected siteConfigService: SiteConfigService,
    protected caveatService: CaveatService,
    protected literatureDataService: LiteratureDataService,
    protected appStateService: AppStateService,
    segmentService: SegmentService,
    protected debugService: DebugService,
    protected favoritesService: FavoritesService,
    protected literatureConfigService: LiteratureGlobalConfigService
  ) {
    super(
      productType,
      cloneDeep(INITIAL_STATE),
      filterService,
      siteConfigService,
      segmentService,
      debugService
    );
    filterService.getData().subscribe(([dataType, data]) => {
      if (dataType === 'filteredList') {
        this.filterProducts(data);
      }
    });
  }

  updateNumberOfRows(numberOfRows: number) {
    this.pagedFunds.pageSize = numberOfRows;
  }

  getPageSize(): number {
    return this.pagedFunds.pageSize;
  }

  getState() {
    return this.state;
  }

  /**
   * Initial method called by ppss component.
   * Initialises service and fetches feed.
   */
  populate(): void {
    if (this.state.hasLoadedFullFeed) {
      // already populated
      this.onPopulated();
    } else {
      this.initialiseState();
      const queries = this.fetchQueries();
      this.fundDataService
        .fetchData(queries, {
          configurationName: this.getProductType(),
          productType: this.getProductTypeParameter(this.getProductType()),
        })
        .pipe(take(1))
        .subscribe((fundListing) => {
          this.ppssStateAction(fundListing);
          this.loadFavourites();
          this.onPopulated();
          this.loadDocuments();
          this.caveatService
            .getPpssAlerts$()
            .pipe(take(1))
            .subscribe((alerts) => this.addAlerts(alerts));
        });
    }
  }

  private onPopulated() {
    // set all rows to display before we initiate filtering
    this.pagedFunds.records = this.state.data.tableRows;
    this.filterService.init(this.state.data.tableRows);
    this.sendNext('populate');
  }

  updateCurrentTab(currentTab) {
    this.state.config.currentTable = currentTab;
    this.sendNext('updateRows');
  }

  /**
   * Fund data handler.
   * Maps the respective values to the needful properties.
   * @param fundListing ppss mf feed received from fund data service.
   */
  ppssStateAction(fundListing: FundListing) {
    const productsData: ProductData[] = this.getProductsData(
      fundListing.products
    );
    this.state.data.tableRows = productsData;
    this.state.data.filteredTableRows = productsData;
    this.state.data.asOfDates.navAsOfDate = fundListing.navAsOfDate;
    this.state.data.asOfDates.performanceAsOfDateMonthly =
      fundListing.performanceAsOfDateMonthly;
    this.state.data.asOfDates.performanceAsOfDateMonthlyStd =
      fundListing.performanceAsOfDateMonthlyStd;
    this.state.data.asOfDates.performanceAsOfDateQuarterly =
      fundListing.performanceAsOfDateQuarterly;
    this.state.data.asOfDates.performanceAsOfDateQuarterlyStd =
      fundListing.performanceAsOfDateQuarterlyStd;
    this.state.data.asOfDates.stdYieldThirtyDayAsofDate =
      fundListing.stdYieldThirtyDayAsofDate;
    this.state.data.asOfDates.ratingsAsOfDate = fundListing.ratingsAsOfDate;

    this.state.config.performanceStatusCaveat =
      fundListing.performanceStatus === 'FINAL'
        ? 'PPSSPerformanceFinalTop'
        : fundListing.performanceStatus === 'PRELIM'
        ? 'PPSSPerformancePreEliminaryTop'
        : null;

    this.state.hasLoadedFullFeed = true;
  }

  private initialiseState() {
    const generalConfig: GeneralConfig = this.siteConfigService.product
      ?.general;
    const performanceConfig: PerformanceConfig = this.siteConfigService.product
      ?.performance;

    // Extract and save performance view options.
    const performanceViewOptions: PerformanceViewOption[] = this.createViewOptions(
      performanceConfig
    );
    this.state.config.performanceViewOptions = performanceViewOptions;

    // Extract and save default performance view.
    const defaultPerformanceView: PerformanceViewOption = performanceViewOptions.find(
      (view) => view.isDefault
    );
    this.state.config.performanceView = defaultPerformanceView;

    // Extract and save period ends.
    const periodEnd: string = defaultPerformanceView?.periodEnds[0];
    this.state.config.periodEnd = periodEnd;

    // NGC-13646: SA performance currency calculation.
    if (this.siteConfigService.getPdsCountry() === 'ZA') {
      this.state.config.showPerformanceCurrency = true;

      this.siteConfigService.getCurrencyListByPerformance().subscribe(
        (currencyList) => {
          this.state.config.performanceCurrencyOptions = currencyList;
          // Here, reference is getting updated (against 'currencyListByPerformance' in site config) with fresh values.
          // Now, no need to 'sendNext'.
        },
        (error) => {
          logger.error(`Error in fetching currency list: ${error}`);
        },
        () => {
          // Intentionally added at first place to have default calculation as BAU.
          this.state.config.performanceCurrencyOptions.splice(
            0,
            0,
            'SHARE_CLASS'
          );
          this.state.config.selectedPerformanceCurrencyOption = this.state.config.performanceCurrencyOptions[0];
        }
      );
    }

    // Save site configurations.
    this.state.isSiteIntl = this.isSiteIntl;
    this.state.siteConfig = this.siteConfigService.product?.site;

    // PPSS tabs visibility.
    this.state.config.tabs.showRatings = this.siteConfigService.showRatings(
      this.getProductType() as ProductType
    );
    this.state.config.tabs.showYield = this.siteConfigService.showYields(
      this.getProductType() as ProductType
    );
    this.state.config.tabs.showFundIdentifiers = this.siteConfigService.showIdentifiers(
      this.getProductType() as ProductType
    );

    // PPSS other visibility conditions.
    this.state.config.hideNavChange = this.siteConfigService.hideNavChange(
      this.getProductType() as ProductType
    );
    this.state.config.hideFactsheet = this.siteConfigService.hideFactsheet(
      this.getProductType() as ProductType
    );

    // NGC-1396

    this.state.config.identifierList = this.siteConfigService.getFundIdentifiers(
      this.getProductType() as ProductType
    );
    // NGC-1351.
    // Suppress performance details for intl, if present.
    if (generalConfig.showMDashForUnder1Year) {
      this.state.config.suppressDetails = {
        showMDashForUnder1Year: generalConfig.showMDashForUnder1Year,
      };
    }
    if (generalConfig.showMDashForUnder6Months) {
      this.state.config.suppressDetails = {
        showMDashForUnder6Months: generalConfig.showMDashForUnder6Months,
      };
    }

    this.state.config.calcType = this.siteConfigService.getDefaultCalcType(
      this.getProductType() as ProductType
    );
  }

  private loadFavourites(): void {
    this.favoritesService.getFavoritesOnLoad$().subscribe((favorites) => {
      this.state.data.tableRows.forEach((product: ProductData, index) => {
        product.isFavoriteFund = false;
        if (
          favorites.includes(
            `${product.fundId}-${product?.shareClass?.identifiers?.shareClassCode}` as FundShareClassId
          )
        ) {
          product.isFavoriteFund = true;
        }
      });
      this.state.data.tableRows = this.sortByFavFund(this.state.data.tableRows);
      this.sendNext('populate');
    });
  }

  private loadDocuments(): void {
    if (this.getProductType() !== ConfigurationId._529) {
      const litParamsFactsheet = new Map();
      const litParamsProfile = new Map();
      litParamsFactsheet.set('type', 'FACT-SHEET');
      litParamsFactsheet.set('cache', 'true');
      const loadFactsheetDocs = this.literatureDataService.loadData(
        litParamsFactsheet,
        false,
        true
      );

      litParamsProfile.set('type', 'PRODUCT-PROFILE');
      litParamsProfile.set('cache', 'true');
      const loadProfileDocs = this.literatureDataService.loadData(
        litParamsProfile,
        false,
        true
      );
      combineLatest([loadFactsheetDocs, loadProfileDocs])
        .pipe(take(1))
        .subscribe(([factsheetDocs, profileDocs]) => {
          if (factsheetDocs && profileDocs) {
            this.addDocuments(factsheetDocs, profileDocs);
          }
        });
    }
  }

  updateFavourite(fund: FundShareClassId): void {
    const selctedIndex = this.state.data.tableRows.findIndex(
      (row) =>
        `${row.fundId}-${row.shareClass.identifiers.shareClassCode}` === fund
    );
    if (this.state.data.tableRows[selctedIndex]) {
      this.state.data.tableRows[selctedIndex].isFavoriteFund = !this.state.data
        .tableRows[selctedIndex]?.isFavoriteFund;
    }

    this.sendNext('updateRows');
  }

  addAlerts(alerts): void {
    if (alerts) {
      this.state.data.tableRows.forEach((row) => {
        row.ppssAlert =
          alerts[`${row.fundId}-${row.shareClass.shareClassCode}`] ||
          alerts[row.fundId];
      });
      this.sendNext('populate'); // TODO can we just refresh one column?
    }
  }

  // we dont have documents for each share class so need to map at fund level as well.
  /* As per the latest literature Api response we only have two records for each fund-
   * one is with frkShareClassCode Z or Adviser, will be mapped for Adviser share class
   * and the second record will be mapped for all other shareclasses
   */

  addDocuments(
    factsheetDocs: LiteratureData,
    profileDocs: LiteratureData
  ): void {
    // to map fund it with primary share class code
    const primaryShareClassMap = new Map<string, string>();
    this.state.data.tableRows.forEach((row) => {
      if (!primaryShareClassMap.get(row.fundId)) {
        if (row.shareClass.primaryShareClass) {
          primaryShareClassMap.set(row.fundId, row.shareClass.shareClassCode);
        }
      }
    });
    // first create map of documents keyed by fundId and shareClassCode
    this.state.data.tableRows.forEach((row) => {
      let primaryShareClassMapLiteratureData: LiteratureDocumentData;
      let docTermsType;
      docTermsType =
        this.getProductType() === ConfigurationId.TIF
          ? 'PRODUCT-PROFILE'
          : 'FACT-SHEET';
      let fundAllDocs = factsheetDocs?.document.filter((doc) => {
        return doc.frkOneTISNumber === row.fundId;
      });
      if (
        ((this.getProductType() === ConfigurationId.SMA ||
          row?.investmentManager?.toLowerCase().includes('franklin')) &&
          fundAllDocs?.length === 0) ||
        this.getProductType() === ConfigurationId.TIF
      ) {
        fundAllDocs = profileDocs?.document.filter((doc) => {
          return doc.frkOneTISNumber === row.fundId;
        });
      }
      // to store primary share class mapped data
      fundAllDocs.forEach((litData) => {
        if (primaryShareClassMap.get(litData.frkOneTISNumber)) {
          const shareClassArray = litData.frkShareClassCode
            ? litData.frkShareClassCode.split(',').map((item) => item.trim())
            : [];
          if (
            shareClassArray.includes(
              primaryShareClassMap.get(litData.frkOneTISNumber)
            )
          ) {
            primaryShareClassMapLiteratureData = cloneDeep(litData);
          }
        }
      });
      let primaryDoc: LiteratureDocumentData;
      if (row.shareClass.shareClassCode === 'Z') {
        primaryDoc = fundAllDocs.find((data) => {
          if (data?.frkShareClassCode) {
            if (data?.frkShareClassCode.match('Z|Advisor')) {
              return data;
            } else if (
              this.literatureConfigService
                .getDocumentMappingConfiguration()
                .get('FACT-SHEET') === 'FUND_ID'
            ) {
              return primaryShareClassMapLiteratureData;
            }
          }
        });
      } else {
        primaryDoc = fundAllDocs.find((data) =>
          data?.frkShareClassCode
            ? this.checkShareClass(
                data,
                row.shareClass.shareClassCode,
                primaryShareClassMapLiteratureData
              )
            : true
        );
      }
      if (this.isSiteIntl) {
        if (primaryDoc?.literatureHref) {
          row.documents = [
            {
              type: primaryDoc.dctermsType,
              link: `${this.appStateService.getLiteratureDownloadBaseUrl()}${
                primaryDoc.literatureHref
              }`,
              showOnPpss: true,
              downloadUrl: primaryDoc.downloadUrl,
            },
          ];
        }
      } else if (primaryDoc?.customFilePath) {
        row.documents = [
          {
            type: primaryDoc.dctermsType,
            link: `${this.appStateService.getLiteratureDownloadUrl()}${
              primaryDoc.customFilePath
            }`,
            showOnPpss: true,
            downloadUrl: primaryDoc.downloadUrl,
          },
        ];
      }
    });
    this.sendNext('populate'); // TODO can we just refresh one column?
  }

  checkShareClass(
    data: LiteratureDocumentData,
    pdsShareClassCode: string,
    primaryShareClassMapLiteratureData: LiteratureDocumentData
  ): LiteratureDocumentData {
    let shareClassArray = data.frkShareClassCode
      .split(',')
      .map((item) => item.trim());
    shareClassArray = this.mapRomanAndNumericShareClass(shareClassArray);
    if (shareClassArray.includes(pdsShareClassCode)) {
      return data;
    }
    // to tag pdf data on fund level
    else if (
      this.literatureConfigService
        .getDocumentMappingConfiguration()
        .get('FACT-SHEET') === 'FUND_ID'
    ) {
      return primaryShareClassMapLiteratureData;
    }
  }

  mapRomanAndNumericShareClass(shareClassArray: string[]): string[] {
    if (shareClassArray.includes('1') && !shareClassArray.includes('I')) {
      shareClassArray.push('I');
    }
    if (shareClassArray.includes('2') && !shareClassArray.includes('II')) {
      shareClassArray.push('II');
    }
    if (shareClassArray.includes('I') && !shareClassArray.includes('1')) {
      shareClassArray.push('1');
    }
    if (shareClassArray.includes('II') && !shareClassArray.includes('2')) {
      shareClassArray.push('2');
    }
    return shareClassArray;
  }
}
